/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render independent Desktop and Mobile student self-leave surfaces over server-authoritative memberships.
 * @state-owner TanStack Query membership cache and local confirmation state
 * @allowed-imports React; active workspace context; classrooms query/mutation APIs; shared loading and error primitives
 * @forbidden direct API clients; client-side authorization decisions; role or Organization fields in commands
 * @public-api StudentClassroomLeave
 * @side-effects authenticated leave command and scoped membership cache invalidation
 * @tests StudentClassroomLeave.test.tsx and frontend/apps/web/e2e/cl-f13.spec.ts
 * @security-boundary student UI to authenticated self-scoped Classroom leave endpoint
 * @data-classification Classroom names and opaque idempotency keys
 * @trust-model list and mutation results are server-authoritative; hidden controls are not authorization
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useOptionalWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useDeleteClassroomMembership } from '../api/classrooms.mutations';
import { useClassrooms } from '../api/classrooms.queries';
import type { ClassroomSummary } from '../model/types';
import './student-classroom-leave.css';

function leaveKey() {
  return `agent-b-cl-f13-leave-${crypto.randomUUID?.() ?? Date.now()}`;
}

function selectedClassroom(
  classrooms: readonly ClassroomSummary[],
  selectedClassroomId: string | undefined,
  selection: string | null,
) {
  if (selectedClassroomId)
    return classrooms.find((classroom) => classroom.id === selectedClassroomId);
  if (!selection) return classrooms[0];
  const index = Number.parseInt(selection.replace(/^c/, ''), 10);
  return Number.isNaN(index)
    ? classrooms.find((classroom) => classroom.id === selection)
    : classrooms[index];
}

function LeaveState({
  classroom,
  mutation,
  onBegin,
  confirming,
  submitting,
  onCancel,
  onConfirm,
  dialogRef,
  triggerRef,
}: {
  classroom: Pick<ClassroomSummary, 'id' | 'name' | 'subject'> | undefined;
  mutation: ReturnType<typeof useDeleteClassroomMembership>;
  onBegin: () => void;
  confirming: boolean;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  dialogRef: { current: HTMLDivElement | null };
  triggerRef: { current: HTMLButtonElement | null };
}) {
  if (!classroom) return <p className="student-classroom-leave__empty">目前沒有可離開的班級</p>;
  return (
    <>
      <div className="student-classroom-leave__classroom">
        <strong>{classroom.name}</strong>
        <span>{classroom.subject}</span>
      </div>
      <button
        aria-label={`離開${classroom.name}`}
        className="bt sm student-classroom-leave__button"
        disabled={confirming}
        onClick={onBegin}
        ref={triggerRef}
        type="button"
      >
        離開班級
      </button>
      {confirming ? (
        <div
          aria-label="確認離開班級"
          aria-modal="true"
          className="student-classroom-leave__dialog"
          ref={dialogRef}
          role="dialog"
        >
          <p>
            確定要離開 <strong>{classroom.name}</strong> 嗎？離開後仍會保留你的學習記錄。
          </p>
          {mutation.isError ? (
            <p className="student-classroom-leave__error" role="alert">
              {hasHttpStatus(mutation.error, 409)
                ? '班級狀態已變更，請重新整理班級清單。'
                : hasHttpStatus(mutation.error, 403)
                  ? '你目前沒有權限離開這個班級。'
                  : hasHttpStatus(mutation.error, 404)
                    ? '這個班級已不存在或你無法查看。'
                    : '班級暫時無法離開，請稍後再試。'}
            </p>
          ) : null}
          <div className="student-classroom-leave__actions">
            <button className="bt sm" disabled={submitting} onClick={onCancel} type="button">
              取消
            </button>
            <button className="bt sm pri" disabled={submitting} onClick={onConfirm} type="button">
              {submitting ? '正在離開…' : '確認離開'}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function StudentClassroomLeaveConnected({
  surface,
  selectedClassroomId,
  children,
}: {
  surface: 'desktop' | 'mobile';
  selectedClassroomId?: string;
  children?: ReactNode;
}) {
  const activeContextId = useActiveContextId();
  const selection = useWorkspaceUiStore((state) => state.selection);
  const go = useWorkspaceUiStore((state) => state.go);
  const [confirmation, setConfirmation] = useState<{
    classroomId: string;
    classroomName: string;
    idempotencyKey: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [left, setLeft] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const classrooms = useClassrooms(activeContextId, { audience: 'student' });
  const mutation = useDeleteClassroomMembership();
  const classroom = selectedClassroom(classrooms.data ?? [], selectedClassroomId, selection);
  // Once the dialog opens, this immutable snapshot is the command target. A
  // later list selection change must never retarget the confirmation.
  const confirmedClassroom = confirmation
    ? ((classrooms.data ?? []).find((item) => item.id === confirmation.classroomId) ?? {
        id: confirmation.classroomId,
        name: confirmation.classroomName,
        subject: '',
      })
    : undefined;

  useEffect(() => {
    if (!confirmation) return;
    const dialog = dialogRef.current;
    dialog?.querySelector<HTMLButtonElement>('button')?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || submitting) return;
      event.preventDefault();
      setConfirmation(null);
      triggerRef.current?.focus();
    };
    dialog?.addEventListener('keydown', handleKeyDown);
    return () => dialog?.removeEventListener('keydown', handleKeyDown);
  }, [confirmation, submitting]);

  if (classrooms.isLoading) return <VispoLoading label="正在載入班級" />;
  if (classrooms.isError) {
    return (
      <p className="student-classroom-leave__message" role="alert">
        {hasHttpStatus(classrooms.error, 403)
          ? '你目前沒有權限查看班級。'
          : '班級清單暫時無法載入，請稍後再試。'}
      </p>
    );
  }

  const confirm = () => {
    if (!confirmation || submitting) return;
    setSubmitting(true);
    mutation.mutate(
      {
        activeContextId,
        classroomId: confirmation.classroomId,
        idempotencyKey: confirmation.idempotencyKey,
      },
      {
        onSuccess: () => {
          setSubmitting(false);
          setConfirmation(null);
          setLeft(true);
          // The list query is invalidated by the mutation hook; clearing the
          // shell selection at the same time prevents stale history from
          // restoring the departed Classroom while refetch is in flight.
          go('room', null);
        },
        onError: () => setSubmitting(false),
      },
    );
  };

  return (
    <div className={`student-classroom-leave-flow student-classroom-leave-flow--${surface}`}>
      <section
        aria-label="離開班級"
        className={`student-classroom-leave student-classroom-leave--${surface}`}
        data-surface={surface}
      >
        <div className="student-classroom-leave__heading">
          <p className="eyebrow">我的班級</p>
          <h2>修正加入的班級</h2>
        </div>
        {left ? <p className="student-classroom-leave__success">已離開這個班級</p> : null}
        <LeaveState
          classroom={confirmedClassroom ?? classroom}
          mutation={mutation}
          dialogRef={dialogRef}
          onBegin={() => {
            if (!classroom) return;
            setConfirmation({
              classroomId: classroom.id,
              classroomName: classroom.name,
              idempotencyKey: leaveKey(),
            });
          }}
          onCancel={() => {
            if (!submitting) {
              setConfirmation(null);
              triggerRef.current?.focus();
            }
          }}
          onConfirm={confirm}
          confirming={Boolean(confirmation)}
          submitting={submitting}
          triggerRef={triggerRef}
        />
      </section>
      {children}
    </div>
  );
}

function StudentClassroomLeaveSurface(props: {
  surface: 'desktop' | 'mobile';
  selectedClassroomId?: string;
  children?: ReactNode;
}) {
  const environment = useOptionalWorkspaceEnvironment();
  if (!environment) return props.children;
  return <StudentClassroomLeaveConnected {...props} />;
}

export function StudentClassroomLeave({
  surface,
  selectedClassroomId,
  children,
}: {
  surface: 'desktop' | 'mobile';
  selectedClassroomId?: string;
  children?: ReactNode;
}) {
  return (
    <StudentClassroomLeaveSurface selectedClassroomId={selectedClassroomId} surface={surface}>
      {children}
    </StudentClassroomLeaveSurface>
  );
}
